import { useState } from "react";
import { FlaskConical, Plus, Search } from "lucide-react";
import { Button } from "~/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "~/components/ui/card";
import { Input } from "~/components/ui/input";
import { ToggleGroup, ToggleGroupItem } from "~/components/ui/toggle-group";
import { evalCasePath, type PartialEvalCase } from "~/services/evals";
import { CaseConfigTable } from "./CaseConfigTable";
import { CaseHealthMatrix } from "./CaseHealthMatrix";
import { ConfirmDeleteDialog } from "./ConfirmDeleteDialog";
import { TableSkeleton } from "./EvalSkeletons";
import { useTableSort } from "./TableSort";
import { CaseView } from "./constants/page";
import { CASE_SORT_DIRECTIONS, CaseSortKey } from "./constants/sorting";
import type { DatasetRefs } from "./primitives/chips";
import { ShownCount } from "./primitives/notices";
import { type CaseHealthCell, type CaseHealthRow, sortCaseRows } from "./selectors/cases";

const VIEW_LABELS: Record<CaseView, string> = {
  [CaseView.HEALTH]: "Health",
  [CaseView.CONFIG]: "Configuration",
};

const VIEW_SUBTITLES: Record<CaseView, (runsPerCase: number) => string> = {
  // "Its own last N" rather than "the last N runs": the window is per case, so
  // two rows can cover entirely different runs, and a reader who assumes
  // otherwise will read down a column and draw the wrong conclusion.
  [CaseView.HEALTH]: (runsPerCase) =>
    `Mean judge score per case, across its own last ${runsPerCase} runs.`,
  [CaseView.CONFIG]: () => "What each case asks, and what makes it pass.",
};

/** Roughly each view's columns, so the placeholder stands where the table will. */
const CASE_COLUMN_WIDTHS: Record<CaseView, string[]> = {
  [CaseView.HEALTH]: ["w-48", "w-16", "w-20", "w-20", "w-56"],
  [CaseView.CONFIG]: ["w-48", "w-24", "w-28", "w-12", "w-16", "w-28", "w-20"],
};

interface EvalCasesTabProps {
  rows: CaseHealthRow[];
  /** How deep each case's history goes, for the subtitle. Not a column count. */
  runsPerCase: number;
  /** Which column set to show. Held in the URL, like the tab itself. */
  view: CaseView;
  onSelectView: (view: CaseView) => void;
  /** Catalog datasets by id, for the configuration view's dataset column. */
  datasets: DatasetRefs;
  /** Cases in the suite before the search box narrowed them, for the count. */
  totalCaseCount: number;
  /** Free-text narrowing of this table, undebounced so the box stays responsive. */
  query: string;
  onQueryChange: (query: string) => void;
  /** Recent runs the caller cannot read, absent from every row. */
  hiddenRunCount: number;
  isLoading: boolean;
  onSelectCase: (caseId: string) => void;
  onSelectCell: (row: CaseHealthRow, cell: CaseHealthCell) => void;
  onNewCase: () => void;
  onDeleted: () => void;
}

/**
 * Every case in the suite, seen through one of two column sets.
 *
 * One table rather than two tabs: health and configuration are the same rows
 * under the same filters, and only the columns differ. So the toggle changes
 * columns, and sort, search, scroll position and row order are shared — flipping
 * the view has to leave the case a reviewer is reading exactly where it was.
 *
 * The search box lives here rather than in the page's filter row because it acts
 * on this table alone, while the facets in that row reach both resources.
 * Beside the count it changes, it also states its own scope: it narrows these
 * rows, not the suite the KPIs measure.
 */
export const EvalCasesTab = ({
  rows,
  runsPerCase,
  view,
  onSelectView,
  datasets,
  totalCaseCount,
  query,
  onQueryChange,
  hiddenRunCount,
  isLoading,
  onSelectCase,
  onSelectCell,
  onNewCase,
  onDeleted,
}: EvalCasesTabProps) => {
  // Lowest score first, in both views. The grid is coloured by score, so its
  // default order should put the cells a reviewer came to look at on top — and
  // holding that order through the toggle is the point of sharing the state.
  const sort = useTableSort<CaseSortKey>(CaseSortKey.SCORE, "asc", CASE_SORT_DIRECTIONS);
  const sorted = sortCaseRows(rows, sort.sortKey, sort.direction);
  const [pendingDelete, setPendingDelete] = useState<PartialEvalCase | null>(null);

  return (
    <Card>
      <CardHeader className="flex flex-row items-start justify-between gap-4 pb-4">
        <div>
          <div className="flex items-baseline gap-2">
            <CardTitle className="text-base font-semibold">Cases</CardTitle>
            <ShownCount shown={rows.length} total={totalCaseCount} noun="case" />
          </div>
          <p className="mt-0.5 text-sm text-muted-foreground">
            {VIEW_SUBTITLES[view](runsPerCase)}
          </p>
        </div>
        <div className="flex shrink-0 items-center gap-3">
          <div className="relative w-[220px]">
            <Search
              className="pointer-events-none absolute top-1/2 left-3 size-4 -translate-y-1/2 text-muted-foreground"
              aria-hidden
            />
            <Input
              value={query}
              onChange={(event) => onQueryChange(event.target.value)}
              placeholder="Filter cases…"
              className="h-9 pl-9"
              aria-label="Filter cases by name or description"
            />
          </div>
          <ToggleGroup
            type="single"
            variant="outline"
            size="sm"
            value={view}
            onValueChange={(next) => {
              // Radix reports "" when the active item is re-clicked. The table
              // always shows one column set, so that is a no-op rather than a
              // third state.
              if (next) {
                onSelectView(next as CaseView);
              }
            }}
            aria-label="Case columns"
          >
            {Object.values(CaseView).map((value) => (
              <ToggleGroupItem
                key={value}
                value={value}
                // `flex-none` undoes the shared item's `flex-1`, whose even
                // split is right for a full-width group but not for this `w-fit`
                // one, where each segment should size to its label. The token
                // default for `data-[state=on]` is too faint against a white card
                // to read as a state, so a primary wash echoes the tab strip.
                className="h-9 flex-none px-4 text-xs font-medium data-[state=on]:bg-primary/10 data-[state=on]:font-semibold data-[state=on]:text-foreground"
              >
                {VIEW_LABELS[value]}
              </ToggleGroupItem>
            ))}
          </ToggleGroup>
          <Button onClick={onNewCase}>
            <Plus className="size-4" aria-hidden />
            New case
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        {isLoading && rows.length === 0 ? (
          <TableSkeleton columns={CASE_COLUMN_WIDTHS[view]} />
        ) : rows.length === 0 ? (
          <EmptyCases onNewCase={onNewCase} />
        ) : view === CaseView.CONFIG ? (
          <CaseConfigTable
            rows={sorted}
            sort={sort}
            datasets={datasets}
            onSelectCase={onSelectCase}
            onDeleteCase={setPendingDelete}
          />
        ) : (
          <CaseHealthMatrix
            rows={sorted}
            sort={sort}
            hiddenRunCount={hiddenRunCount}
            onSelectCase={onSelectCase}
            onSelectCell={onSelectCell}
          />
        )}
      </CardContent>

      <ConfirmDeleteDialog
        open={pendingDelete != null}
        onOpenChange={(open) => !open && setPendingDelete(null)}
        title={`Delete ${pendingDelete?.name ?? "this case"}?`}
        description={
          <>
            The case and its definition go for good. Results already recorded in past runs stay, but
            lose their definition.
          </>
        }
        confirmLabel="Delete case"
        path={pendingDelete ? evalCasePath(pendingDelete.id) : null}
        errorMessage="Could not delete the case."
        onDeleted={onDeleted}
      />
    </Card>
  );
};

/**
 * Empty state that teaches the surface: cases are imported from the YAML suites,
 * so "no cases" usually means the import has not run here. Reached only once the
 * suite has loaded, so it can state a finding rather than hedge.
 */
const EmptyCases = ({ onNewCase }: { onNewCase: () => void }) => (
  <div className="flex flex-col items-center gap-2 py-12 text-center">
    <FlaskConical className="size-8 text-muted-foreground/60" aria-hidden />
    <p className="text-sm font-medium text-foreground">No eval cases match this filter</p>
    <p className="max-w-md text-sm text-muted-foreground">
      Cases usually arrive from the suites under{" "}
      <code className="font-mono text-xs">tests/evals/configs</code> via the importer. You can also
      write one here.
    </p>
    <Button variant="outline" onClick={onNewCase} className="mt-2">
      <Plus className="size-4" aria-hidden />
      New case
    </Button>
  </div>
);
